import type { StatusTone } from "@/components/ui";
import type {
  MarketplaceHealthSeverity,
  MarketplaceHealthSignalStatus,
  MarketplaceOpportunityAction,
  MarketplaceOpportunityStatus,
  MarketplaceSignalAction,
  Priority,
} from "./types";

export function formatLabel(value: string): string {
  return value.replaceAll("_", " ");
}

export function signalStatusTone(
  status: MarketplaceHealthSignalStatus,
): StatusTone {
  switch (status) {
    case "detected":
      return "warning";
    case "validated":
    case "monitoring":
      return "info";
    case "action_required":
      return "danger";
    case "resolved":
      return "success";
    case "archived":
      return "neutral";
    default:
      return "neutral";
  }
}

export function severityTone(severity: MarketplaceHealthSeverity): StatusTone {
  switch (severity) {
    case "low":
    case "normal":
      return "neutral";
    case "high":
      return "warning";
    case "urgent":
    case "critical":
      return "danger";
    default:
      return "neutral";
  }
}

export function opportunityStatusTone(
  status: MarketplaceOpportunityStatus,
): StatusTone {
  switch (status) {
    case "identified":
    case "validated":
      return "info";
    case "prioritized":
    case "assigned":
    case "in_progress":
      return "warning";
    case "realized":
      return "success";
    case "declined":
    case "closed":
      return "neutral";
    default:
      return "neutral";
  }
}

export function priorityTone(priority: Priority): StatusTone {
  return severityTone(priority);
}

export const SIGNAL_ACTIONS_FROM: Record<
  MarketplaceSignalAction,
  readonly MarketplaceHealthSignalStatus[]
> = {
  validate: ["detected"],
  monitor: ["validated", "action_required"],
  require_action: ["validated", "monitoring"],
  resolve: ["validated", "monitoring", "action_required"],
  archive: [
    "detected",
    "validated",
    "monitoring",
    "action_required",
    "resolved",
  ],
  revalidate: ["monitoring", "action_required"],
};

export const OPPORTUNITY_ACTIONS_FROM: Record<
  MarketplaceOpportunityAction,
  readonly MarketplaceOpportunityStatus[]
> = {
  validate: ["identified"],
  prioritize: ["validated"],
  assign: ["prioritized", "assigned"],
  start: ["assigned"],
  realize: ["in_progress", "assigned"],
  decline: [
    "identified",
    "validated",
    "prioritized",
    "assigned",
    "in_progress",
  ],
  close: [
    "identified",
    "validated",
    "prioritized",
    "assigned",
    "in_progress",
    "realized",
    "declined",
  ],
};

export function allowedSignalActions(
  status: MarketplaceHealthSignalStatus,
): MarketplaceSignalAction[] {
  return (
    Object.keys(SIGNAL_ACTIONS_FROM) as MarketplaceSignalAction[]
  ).filter((action) => SIGNAL_ACTIONS_FROM[action].includes(status));
}

export function allowedOpportunityActions(
  status: MarketplaceOpportunityStatus,
): MarketplaceOpportunityAction[] {
  return (
    Object.keys(OPPORTUNITY_ACTIONS_FROM) as MarketplaceOpportunityAction[]
  ).filter((action) => OPPORTUNITY_ACTIONS_FROM[action].includes(status));
}
